import React, { useEffect, useState } from 'react';
import { insurerService } from '../../services/insurerService';

export interface RevokeTargetClient {
  id: number;
  email: string;
  full_name?: string | null;
  credits_remaining?: number;
  is_registered?: boolean;
}

export interface RevokeTokenModalProps {
  isOpen: boolean;
  client: RevokeTargetClient | null;
  onClose: () => void;
  onRevoked: (message: string) => void;
  onError?: (message: string) => void;
  /**
   * Optional custom action override (e.g. for superadmin calling adminService)
   */
  onConfirm?: () => Promise<string | void>;
}

const RevokeTokenModal: React.FC<RevokeTokenModalProps> = ({
  isOpen,
  client,
  onClose,
  onRevoked,
  onError,
  onConfirm,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setModalError(null);
    }
  }, [isOpen, client?.id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting, onClose]);

  if (!isOpen || !client) return null;

  const handleRevoke = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setModalError(null);

    try {
      if (onConfirm) {
        const customMsg = await onConfirm();
        onRevoked(typeof customMsg === 'string' ? customMsg : 'Partner credit revoked successfully');
      } else {
        const result = await insurerService.revokeClientCredit(client.id);
        onRevoked(result.message || 'Partner credit revoked successfully');
      }
      onClose();
    } catch (err: any) {
      const errDetail = err?.response?.data?.detail || err?.message || 'Failed to revoke partner token';
      setModalError(errDetail);
      if (onError) {
        onError(errDetail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const creditsCount = client.credits_remaining ?? 1;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
          onClick={submitting ? undefined : onClose}
          aria-hidden="true"
        />

        {/* Modal Dialog */}
        <div className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl transition-all">
          <form onSubmit={handleRevoke}>
            {/* Header with Danger Icon */}
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Revoke Tabletop Token</h3>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                    aria-label="Close"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to revoke the unused token for this client?
                </p>
              </div>
            </div>

            {/* Error banner if action fails */}
            {modalError && (
              <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2">
                <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{modalError}</span>
              </div>
            )}

            {/* Client Context Details */}
            <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50/70 p-4 text-sm space-y-2">
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium text-gray-500">Client:</span>
                <span className="font-semibold text-gray-900">{client.email}</span>
              </div>
              {client.full_name && (
                <div className="flex justify-between items-center text-gray-700">
                  <span className="font-medium text-gray-500">Name:</span>
                  <span className="text-gray-900">{client.full_name}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium text-gray-500">Unused Tokens to Revoke:</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                  {creditsCount} {creditsCount === 1 ? 'Token' : 'Tokens'}
                </span>
              </div>
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium text-gray-500">Account Status:</span>
                <span className="text-xs font-medium text-gray-600">
                  {client.is_registered ? 'Registered Client' : 'Pending Invitation'}
                </span>
              </div>
            </div>

            {/* Impact Explanation */}
            <div className="mt-4 rounded-lg bg-amber-50/80 border border-amber-200/80 p-3 text-xs text-amber-900 space-y-1">
              <p className="font-semibold text-amber-950 flex items-center gap-1.5">
                <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Important notice
              </p>
              <ul className="list-disc list-inside text-amber-800 pl-1 space-y-0.5">
                <li>The unused credit will be immediately deducted from the client's balance.</li>
                <li>The partner credit ledger invoice entry will be voided.</li>
                <li>The client's account and invitation link will remain intact, and you can grant a new token at any time.</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Keep Token
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 shadow-sm transition"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Revoking…
                  </>
                ) : (
                  <>Revoke Token</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RevokeTokenModal;
